import React, { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLikes } from "@/hooks/useLikes";
import { useCommentCount } from "@/hooks/useCommentCount";
import { useViewCount } from "@/hooks/useViewCount";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { sanitizeHtml } from "@/lib/htmlSanitizer";
import BlogPostForm from "../BlogPostForm";
import CommentSection from "../CommentSection";
import Logo from "../Logo";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Edit,
  Plus,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  FileText,
  Heart,
  Trash2,
  Share2,
  Tag as TagIcon,
  X,
  Bell,
  BellOff,
  Video,
  AtSign,
  MapPin,
  ImagePlus,
  Camera,
  Cloud,
  MessagesSquare,
  Mail,
  Hash,
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import AuthModal from "../AuthModal";
import WelcomeBanner from "../WelcomeBanner";
import { PollRenderer } from "../polls/PollRenderer";
import tiktokIcon from "@/assets/social/tiktok.svg";
import substackIcon from "@/assets/social/substack.jpg";
import blueskyIcon from "@/assets/social/bluesky.webp";
import redditIcon from "@/assets/social/reddit.png";
import discordIcon from "@/assets/social/discord.svg";
import pinterestIcon from "@/assets/social/pinterest.png";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  preview: string;
  image_url?: string;
  facebook_url?: string;
  twitter_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  youtube_url?: string;
  tiktok_url?: string;
  threads_url?: string;
  google_business_url?: string;
  pinterest_url?: string;
  snapchat_url?: string;
  bluesky_url?: string;
  reddit_url?: string;
  substack_url?: string;
  discord_url?: string;
  published: boolean;
  published_at?: string;
  created_at: string;
  user_id: string;
  profiles?: {
    display_name: string;
  } | null;
  post_tags?: {
    tag_id: string;
    tags: {
      id: string;
      name: string;
      slug: string;
    };
  }[];
}

type CardState = "hidden" | "docked" | "fullscreen";

interface BlogCardProps {
  cardState: CardState;
  isMobile: boolean;
  onCardStateChange?: (state: CardState) => void;
  onToggleCard?: () => void;
  onHideCard: () => void;
}

const BlogCard: React.FC<BlogCardProps> = ({ cardState, isMobile, onCardStateChange, onToggleCard, onHideCard }) => {
  const { user, isAdmin, isReviewer, loading: authLoading } = useAuth();
  const { sessionKey } = useSessionTracking();
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPost, setLoadingPost] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<BlogPost | null>(null);
  const [sortOrder, setSortOrder] = useState<"latest" | "oldest">("latest");
  const [selectedTagFilter, setSelectedTagFilter] = useState<string | null>(null);
  const [availableTags, setAvailableTags] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { isSubscribed, toggleSubscription, loading: subscriptionsLoading } = useSubscriptions(user?.id);
  const [showPublishWarning, setShowPublishWarning] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [postToPublish, setPostToPublish] = useState<string | null>(null);
  const [showPublishDateDialog, setShowPublishDateDialog] = useState(false);
  const [postToRepublish, setPostToRepublish] = useState<BlogPost | null>(null);
  const [isResisting, setIsResisting] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [hasSlideInPlayed, setHasSlideInPlayed] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const commentsRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    // Delay blog card to appear after chat card completes
    const timer = setTimeout(() => {
      setHasMounted(true);
      setHasSlideInPlayed(true);
    }, 3300);
    return () => clearTimeout(timer);
  }, []);

  // Fetch available tags
  useEffect(() => {
    const fetchAvailableTags = async () => {
      try {
        let query = supabase.from("post_tags").select(`
            tags (
              id,
              name,
              slug
            ),
            blog_posts!inner (
              published
            )
          `);

        // If not admin or reviewer, only show tags with published posts
        if (!isAdmin && !isReviewer) {
          query = query.eq("blog_posts.published", true);
        }

        const { data, error } = await query;

        if (error) throw error;

        // Extract unique tags and sort alphabetically
        const tagMap = new Map();
        data?.forEach((item: any) => {
          if (item.tags) {
            tagMap.set(item.tags.id, {
              id: item.tags.id,
              name: item.tags.name,
              slug: item.tags.slug,
            });
          }
        });

        const uniqueTags = Array.from(tagMap.values()).sort((a, b) => a.name.localeCompare(b.name));

        setAvailableTags(uniqueTags);
      } catch (error: any) {
        console.error("Error fetching tags:", error);
      }
    };

    if (!authLoading) {
      fetchAvailableTags();
    }
  }, [authLoading, isAdmin, isReviewer]);

  useEffect(() => {
    // Only fetch posts after auth is fully loaded
    if (!authLoading) {
      fetchBlogPosts();
    }
  }, [user, isAdmin, isReviewer, authLoading, sortOrder, selectedTagFilter]);

  // Check URL params for deep linking to a specific post
  useEffect(() => {
    const checkUrlParams = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const postId = urlParams.get("post");

      if (postId && blogPosts.length > 0 && !selectedPost) {
        const post = blogPosts.find((p) => p.id === postId);
        if (post) {
          handlePostClick(post);
        }
      }
    };

    checkUrlParams();
  }, [blogPosts]);

  const fetchBlogPosts = async () => {
    console.log("fetchBlogPosts called, user:", user?.id);
    console.log("Auth state:", { isSignedIn: !!user, isAdmin });
    try {
      // If filtering by tag, first get post IDs with that tag
      let filteredPostIds: string[] | null = null;

      if (selectedTagFilter) {
        // Step 1: Get tag ID from slug
        const { data: tagData, error: tagError } = await supabase
          .from("tags")
          .select("id")
          .eq("slug", selectedTagFilter)
          .single();

        if (tagError || !tagData) {
          console.log("Tag not found:", selectedTagFilter);
          setBlogPosts([]);
          setLoading(false);
          return;
        }

        // Step 2: Get post IDs with this tag
        let postTagsQuery;

        if (!isAdmin && !isReviewer) {
          // For regular users: only get tags from published posts
          postTagsQuery = supabase
            .from("post_tags")
            .select("blog_post_id, blog_posts!inner(published)")
            .eq("tag_id", tagData.id)
            .eq("blog_posts.published", true);
        } else {
          // For admins/reviewers: get all post IDs with this tag (including unpublished)
          postTagsQuery = supabase.from("post_tags").select("blog_post_id").eq("tag_id", tagData.id);
        }

        const { data: postTagsData, error: postTagsError } = await postTagsQuery;

        if (postTagsError) throw postTagsError;

        filteredPostIds = postTagsData?.map((pt: any) => pt.blog_post_id) || [];

        if (filteredPostIds.length === 0) {
          setBlogPosts([]);
          setLoading(false);
          return;
        }
      }

      let query = supabase.from("blog_posts").select(`
          id,
          title,
          content,
          preview,
          image_url,
          facebook_url,
          post_tags (
            tag_id,
            tags (
              id,
              name,
              slug
            )
          ),
          twitter_url,
          instagram_url,
          linkedin_url,
          youtube_url,
          tiktok_url,
          reddit_url,
          substack_url,
          discord_url,
          threads_url,
          google_business_url,
          pinterest_url,
          snapchat_url,
          bluesky_url,
          published,
          published_at,
          created_at,
          user_id
        `);

      // If not admin or reviewer, only show published posts
      if (!isAdmin && !isReviewer) {
        query = query.eq("published", true);
      }

      // Apply tag filter if present
      if (filteredPostIds !== null) {
        query = query.in("id", filteredPostIds);
      }

      const { data, error } = await query.order("published_at", { ascending: sortOrder === "oldest", nullsFirst: false });

      console.log("Blog posts fetch result:", { data, error });
      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }
      setBlogPosts(data || []);
      console.log("Blog posts set:", data?.length || 0, "posts");
    } catch (error: any) {
      console.error("Error fetching blog posts:", error);
      toast.error(`Error loading posts: ${error.message}`);
    } finally {
      setLoading(false);
      console.log("Loading set to false");
    }
  };

  const handleCreatePost = () => {
    setEditingPost(null);
    setShowForm(true);
  };

  const handleEditPost = (post: BlogPost) => {
    setEditingPost(post);
    setShowForm(true);
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditingPost(null);
    fetchBlogPosts();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingPost(null);
  };

  const handlePostClick = (post: BlogPost) => {
    // Store current scroll position
    if (contentRef.current) {
      scrollPositionRef.current = contentRef.current.scrollTop;
    }

    setLoadingPost(true);
    setSelectedPost(post);

    // Update URL with post ID for deep linking
    window.history.pushState({}, "", `${window.location.pathname}?post=${post.id}`);

    // Scroll to top of content
    setTimeout(() => {
      contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      setLoadingPost(false);
    }, 100);

    // Track the view when opening a post
    const trackView = async () => {
      // Only track if we have either user_id or session_key (RLS requirement)
      if (!user?.id && !sessionKey) {
        console.log("Skipping view tracking - no user_id or session_key");
        return;
      }

      try {
        await supabase.from("views").insert({
          blog_post_id: post.id,
          user_id: user?.id || null,
          session_key: sessionKey || null,
          ip_address: null,
          user_agent: navigator.userAgent,
          association_status: "pending",
        });
      } catch (error: any) {
        console.error("Error tracking view:", error);
      }
    };

    trackView();
  };

  const handleBackToList = () => {
    setSelectedPost(null);

    // Clear URL parameter
    window.history.pushState({}, "", window.location.pathname);

    // Restore scroll position
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: scrollPositionRef.current, behavior: "smooth" });
      }
    }, 100);
  };

  const handleTogglePublish = async (post: BlogPost, e: React.MouseEvent) => {
    e.stopPropagation();

    // If publishing (currently unpublished)
    if (!post.published) {
      // Check if this is a republish (was previously published)
      if (post.published_at) {
        // Show republish dialog to ask about date update
        setPostToRepublish(post);
        setShowPublishDateDialog(true);
      } else {
        // First time publishing - show warning with subscriber count
        try {
          const { data: count, error } = await supabase.rpc("get_subscriber_count", {
            subscription_type_param: "new_post",
          });

          if (error) {
            console.error("Error fetching subscriber count:", error);
          }

          setSubscriberCount(count || 0);
          setPostToPublish(post.id);
          setShowPublishWarning(true);
        } catch (error) {
          console.error("Error checking subscribers:", error);
          toast.error("Failed to check subscriber count");
        }
      }
    } else {
      // Unpublishing doesn't need warning
      await performPublishToggle(post, false);
    }
  };

  const performPublishToggle = async (post: BlogPost, updatePublishDate: boolean = true) => {
    try {
      const updateData: any = { published: !post.published };
      
      // If publishing and should update the date
      if (!post.published && updatePublishDate) {
        updateData.published_at = new Date().toISOString();
      }
      
      const { error } = await supabase.from("blog_posts").update(updateData).eq("id", post.id);

      if (error) throw error;

      toast.success(`Post ${!post.published ? "published" : "unpublished"} successfully`);
      fetchBlogPosts();
    } catch (error: any) {
      console.error("Error toggling publish status:", error);
      toast.error(`Error ${!post.published ? "publishing" : "unpublishing"} post: ${error.message}`);
    }
  };

  const confirmPublish = async () => {
    if (postToPublish) {
      const post = blogPosts.find((p) => p.id === postToPublish);
      if (post) {
        await performPublishToggle(post, true);
      }
      setShowPublishWarning(false);
      setPostToPublish(null);
    }
  };

  const handleRepublishWithDate = async (updateDate: boolean) => {
    if (postToRepublish) {
      // If updating date, show subscriber warning first
      if (updateDate) {
        try {
          const { data: count, error } = await supabase.rpc("get_subscriber_count", {
            subscription_type_param: "new_post",
          });

          if (error) {
            console.error("Error fetching subscriber count:", error);
          }

          setSubscriberCount(count || 0);
          setPostToPublish(postToRepublish.id);
          setShowPublishDateDialog(false);
          setShowPublishWarning(true);
        } catch (error) {
          console.error("Error checking subscribers:", error);
          toast.error("Failed to check subscriber count");
        }
      } else {
        // Keep original date, just republish without notification
        await performPublishToggle(postToRepublish, false);
        setShowPublishDateDialog(false);
        setPostToRepublish(null);
      }
    }
  };

  const handleDeleteClick = (post: BlogPost, e: React.MouseEvent) => {
    e.stopPropagation();
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };

  const handleDeletePost = async () => {
    if (!postToDelete) return;

    try {
      const { error } = await supabase.from("blog_posts").delete().eq("id", postToDelete.id);

      if (error) throw error;

      toast.success("Post deleted successfully");
      setDeleteDialogOpen(false);
      setPostToDelete(null);

      // If we're viewing the deleted post, go back to list
      if (selectedPost?.id === postToDelete.id) {
        setSelectedPost(null);
      }

      fetchBlogPosts();
    } catch (error: any) {
      console.error("Error deleting post:", error);
      toast.error(`Error deleting post: ${error.message}`);
    }
  };

  const handleShare = async (post: BlogPost) => {
    const shareUrl = `${window.location.origin}/post/${post.id}`;
    const shareData = {
      title: post.title,
      text: post.preview,
      url: shareUrl,
    };

    // Try Web Share API first (works on mobile)
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success("Post shared successfully");
      } catch (error: any) {
        if (error.name !== "AbortError") {
          console.error("Error sharing:", error);
        }
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied to clipboard");
      } catch (error) {
        console.error("Error copying to clipboard:", error);
        toast.error("Failed to copy link");
      }
    }
  };

  const getSocialIcon = (platform: string) => {
    const customIcons = {
      tiktok: tiktokIcon,
      substack: substackIcon,
      bluesky: blueskyIcon,
      reddit: redditIcon,
      discord: discordIcon,
      pinterest: pinterestIcon,
    };

    const lucideIcons = {
      facebook: Facebook,
      twitter: Twitter,
      instagram: Instagram,
      linkedin: Linkedin,
      youtube: Youtube,
      threads: AtSign,
      google_business: MapPin,
      snapchat: Camera,
    };

    // Return custom icon path if available
    if (customIcons[platform as keyof typeof customIcons]) {
      return customIcons[platform as keyof typeof customIcons];
    }

    // Return Lucide icon component if available
    return lucideIcons[platform as keyof typeof lucideIcons] || null;
  };

  const renderSocialLinks = (post: BlogPost) => {
    const socialPlatforms = [
      "facebook",
      "twitter",
      "instagram",
      "linkedin",
      "youtube",
      "tiktok",
      "threads",
      "google_business",
      "pinterest",
      "snapchat",
      "bluesky",
      "reddit",
      "substack",
      "discord",
    ];

    return (
      <div className="flex flex-wrap gap-2 mt-2 justify-center sm:justify-start">
        {socialPlatforms.map((platform) => {
          const url = post[`${platform}_url` as keyof BlogPost] as string;
          const iconData = getSocialIcon(platform);

          if (url && iconData) {
            // Check if it's a custom icon (string path) or Lucide icon (React component)
            const isCustomIcon = typeof iconData === "string";

            return (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors flex-shrink-0"
                aria-label={`Visit us on ${platform.charAt(0).toUpperCase() + platform.slice(1).replace("_", " ")}`}
              >
                {isCustomIcon ? (
                  <img src={iconData} alt={platform} className="w-4 h-4 object-contain" />
                ) : (
                  React.createElement(iconData, { className: "w-4 h-4" })
                )}
              </a>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  // Show the blog post form dialog
  if (showForm) {
    return (
      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <BlogPostForm post={editingPost || undefined} onSave={handleFormSave} onCancel={handleFormCancel} />
        </DialogContent>
      </Dialog>
    );
  }

  // Render the blog card based on its state
  if (cardState === "docked") {
    return (
      <div
        className="fixed bottom-0 left-4 right-4 h-8 bg-blog-white flex items-center justify-between cursor-pointer shadow-xl hover:bg-hover transition-colors z-30 border-2 border-b-0 border-blog-border rounded-t-lg px-4 animate-gentle-bounce"
        onClick={onToggleCard}
      >
        <span className="text-foreground font-medium text-sm">Blog</span>
        <div className="flex flex-col gap-0.5 absolute left-1/2 transform -translate-x-1/2">
          <div className="w-6 h-0.5 bg-foreground/60 rounded"></div>
          <div className="w-6 h-0.5 bg-foreground/60 rounded"></div>
          <div className="w-6 h-0.5 bg-foreground/60 rounded"></div>
        </div>
      </div>
    );
  }

  const baseClasses =
    "fixed bg-blog-white border-2 border-blog-border shadow-xl transition-all duration-300 ease-in-out z-40 overflow-hidden flex flex-col";

  let positionClasses = "";
  let sizeClasses = "";
  let roundingClasses = "rounded-lg";

  if (isMobile) {
    if (cardState === "fullscreen") {
      positionClasses = "top-0 left-0 right-0 bottom-0";
      sizeClasses = "h-full w-full rounded-none";
    }
  } else {
    if (cardState === "fullscreen") {
      positionClasses = "top-8 left-4 bottom-6 right-4";
      sizeClasses = "";
      roundingClasses = "rounded-lg";
    }
  }

  const PostLikes = ({ postId, content, post }: { postId: string; content: string; post: BlogPost }) => {
    const { sessionKey } = useSessionTracking();
    const { likeCount, isLiked, toggleLike } = useLikes(postId, "post");
    const { commentCount } = useCommentCount(postId);
    const { viewCount, trackView } = useViewCount(postId);

    // Track view when component mounts
    useEffect(() => {
      trackView(sessionKey);
    }, []);

    // Calculate word count by stripping HTML and counting words
    const wordCount = React.useMemo(() => {
      const text = content
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      return text.split(" ").filter((word) => word.length > 0).length;
    }, [content]);

    // Get social platforms with URLs
    const socialPlatforms = [
      "facebook",
      "twitter",
      "instagram",
      "linkedin",
      "youtube",
      "tiktok",
      "threads",
      "google_business",
      "pinterest",
      "snapchat",
      "bluesky",
      "reddit",
      "substack",
      "discord",
    ];
    const socialLinks = socialPlatforms
      .map((platform) => {
        const url = post[`${platform}_url` as keyof BlogPost] as string;
        const iconData = getSocialIcon(platform);
        if (url && iconData) {
          return { platform, url, iconData };
        }
        return null;
      })
      .filter(Boolean);

    return (
      <div className="mt-2">
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleLike(sessionKey);
            }}
            className={`flex items-center gap-1 hover:text-red-500 transition-colors ${
              isLiked ? "text-red-500" : "text-muted-foreground"
            }`}
          >
            <Heart className={`w-4 h-4 ${isLiked ? "fill-current" : ""}`} />
            <span>
              {likeCount} {likeCount === 1 ? "like" : "likes"}
            </span>
          </button>
          <span>
            👁️ {viewCount} {viewCount === 1 ? "view" : "views"}
          </span>
          <span>
            💬 {commentCount} {commentCount === 1 ? "comment" : "comments"}
          </span>
          <span>
            📝 {wordCount} {wordCount === 1 ? "word" : "words"}
          </span>
        </div>

        {/* Social media icons on separate line */}
        {socialLinks.length > 0 && (
          <div className="flex gap-2 mt-2">
            {socialLinks.map((link: any) => {
              const isCustomIcon = typeof link.iconData === "string";
              return (
                <a
                  key={link.platform}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors flex-shrink-0"
                  aria-label={`Visit us on ${link.platform.charAt(0).toUpperCase() + link.platform.slice(1).replace("_", " ")}`}
                >
                  {isCustomIcon ? (
                    <img src={link.iconData} alt={link.platform} className="w-3.5 h-3.5 object-contain" />
                  ) : (
                    React.createElement(link.iconData, { className: "w-3.5 h-3.5" })
                  )}
                </a>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`
        ${baseClasses} ${positionClasses} ${sizeClasses} ${roundingClasses}
        transform-gpu will-change-transform
        ${!hasSlideInPlayed && hasMounted ? "animate-slide-in-up-slow" : ""}
        ${!hasMounted ? "translate-y-full opacity-0" : ""}
        ${isResisting ? "animate-bounce-resist-vertical" : ""}
        flex flex-col
      `}
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div
          className="flex flex-col md:flex-row md:items-center md:justify-between p-3 border-b border-border bg-muted/50 transition-colors cursor-pointer hover:bg-muted/70"
          onClick={() => {
            console.log("Header clicked - starting resist animation");
            // Phase 1: Resist dismiss and show toast
            setIsResisting(true);

            // Wait for animation to complete, then hide card
            setTimeout(() => {
              console.log("Animation complete - hiding card");
              setIsResisting(false);
            }, 500);

            // Show toast and hide card slightly after animation
            setTimeout(() => {
              onHideCard();
            }, 550);
          }}
        >
          {selectedPost ? (
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleBackToList();
                }}
                className="text-primary hover:text-primary/80 text-base font-semibold flex items-center gap-2"
              >
                ← Back to List View
              </button>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditPost(selectedPost);
                  }}
                  className="gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShare(selectedPost);
                }}
                className="gap-2"
              >
                <Share2 className="w-4 h-4" />
                Share
              </Button>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              {/* Row 1 on mobile: Title + Bell */}
              <div className="flex items-center justify-between w-full md:w-auto md:justify-start md:gap-3">
                <h2 className="text-base font-semibold">Blog</h2>
                
                {/* Notification bell for mobile only */}
                <div className="md:hidden">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!user) {
                        setShowAuthModal(true);
                        return;
                      }
                      await toggleSubscription("new_post");
                    }}
                    disabled={subscriptionsLoading}
                    className="gap-2"
                  >
                    {isSubscribed("new_post") ? (
                      <>
                        <span className="sm:hidden">Unfollow</span>
                        <BellOff className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        <span className="sm:hidden">Follow</span>
                        <Bell className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Row 2 on mobile, inline on desktop: Filters + Sort */}
              <div className="flex items-center gap-3 flex-wrap mt-2 md:mt-0">
                {/* Tag Filter */}
                <div className="flex items-center gap-2">
                  <TagIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  <Select
                    value={selectedTagFilter || "all"}
                    onValueChange={(value) => {
                      setSelectedTagFilter(value === "all" ? null : value);
                      contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    <SelectTrigger className="w-[140px] h-7 text-xs" aria-label="Filter posts by topic">
                      <SelectValue placeholder="All Topics" />
                    </SelectTrigger>
                    <SelectContent className="bg-background z-50">
                      <SelectItem value="all">All Topics</SelectItem>
                      {availableTags.map((tag) => (
                        <SelectItem key={tag.id} value={tag.slug}>
                          {tag.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {selectedTagFilter && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTagFilter(null);
                      }}
                      className="h-7 px-2 gap-1"
                    >
                      <X className="w-3 h-3" />
                      Clear
                    </Button>
                  )}
                </div>

                {/* Sort Order */}
                <select
                  value={sortOrder}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSortOrder(e.target.value as "latest" | "oldest");
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs px-2 py-1 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-focus h-7"
                  aria-label="Sort posts by date"
                >
                  <option value="latest">Latest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>
            </div>
          )}
          <div className="hidden md:flex items-center gap-2">
            {selectedPost && (
              <Button
                size="sm"
                variant="ghost"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!user) {
                    setShowAuthModal(true);
                    return;
                  }
                  await toggleSubscription("new_comment", selectedPost.id);
                }}
                disabled={subscriptionsLoading}
                className="gap-2"
              >
                {isSubscribed("new_comment", selectedPost.id) ? (
                  <>
                    <span className="hidden sm:inline">Unfollow Comments</span>
                    <span className="sm:hidden">Unfollow</span>
                    <BellOff className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Follow Comments</span>
                    <span className="sm:hidden">Follow</span>
                    <Bell className="w-4 h-4" />
                  </>
                )}
              </Button>
            )}
            {!selectedPost && (
              <Button
                size="sm"
                variant="ghost"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!user) {
                    setShowAuthModal(true);
                    return;
                  }
                  await toggleSubscription("new_post");
                }}
                disabled={subscriptionsLoading}
                className="gap-2"
              >
                {isSubscribed("new_post") ? (
                  <>
                    <span className="hidden sm:inline">Unfollow New Posts</span>
                    <span className="sm:hidden">Unfollow</span>
                    <BellOff className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Follow New Posts</span>
                    <span className="sm:hidden">Follow</span>
                    <Bell className="w-4 h-4" />
                  </>
                )}
              </Button>
            )}
            {isAdmin && !selectedPost && (
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreatePost();
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                New Post
              </Button>
            )}
          </div>
        </div>

        {/* Welcome Banner */}
        <WelcomeBanner />

        {/* Content */}
        <div ref={contentRef} className="flex-1 overflow-y-auto relative">
          {/* Loading spinner overlay */}
          {loadingPost && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
              <Logo size={64} animate="pulse" />
            </div>
          )}

          {loading ? (
            <div className="p-4 text-center">Loading...</div>
          ) : !selectedPost ? (
            // Blog list view
            <div className="p-4 space-y-4 max-w-[800px] mx-auto">
              {/* Filter Results Summary */}
              {selectedTagFilter && (
                <div className="mb-2 text-sm text-muted-foreground">
                  Showing {blogPosts.length} post{blogPosts.length !== 1 ? "s" : ""} with tag:
                  <span className="font-medium text-foreground ml-1">
                    {availableTags.find((t) => t.slug === selectedTagFilter)?.name}
                  </span>
                </div>
              )}

              {blogPosts.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <p>No blog posts yet.</p>
                  {isAdmin && (
                    <Button onClick={handleCreatePost} className="mt-4">
                      Create your first post
                    </Button>
                  )}
                </div>
              ) : (
                blogPosts.map((post, index) => (
                  <div
                    key={post.id}
                    className="border border-border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handlePostClick(post)}
                  >
                    <div className={`flex flex-col sm:flex-row gap-4 ${index % 2 === 1 ? "sm:flex-row-reverse" : ""}`}>
                      {/* Image column */}
                      <div className="flex flex-col items-center sm:items-start flex-shrink-0">
                        <div className="w-36 h-36 overflow-hidden rounded bg-muted/30">
                          {post.image_url ? (
                            <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-muted/50 flex items-center justify-center opacity-30">
                              <Logo size={32} className="grayscale" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Text content column */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h3 className="font-semibold text-sm">{post.title}</h3>
                            <PostLikes postId={post.id} content={post.content} post={post} />
                          </div>
                          {isAdmin && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => handleTogglePublish(post, e)}
                                className="text-xs px-2"
                              >
                                {post.published ? "Unpublish" : "Publish"}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditPost(post);
                                }}
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={(e) => handleDeleteClick(post, e)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{formatDate(post.published_at || post.created_at)}</p>
                        <p className="text-xs text-muted-foreground line-clamp-5 md:line-clamp-3">{post.preview}</p>
                        {/* Tags */}
                        {post.post_tags && post.post_tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {post.post_tags.slice(0, 5).map((pt) => (
                              <button
                                key={pt.tag_id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedTagFilter(pt.tags.slug);
                                  contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                                className="inline-flex items-center gap-1 bg-primary/10 text-primary border border-primary/20 rounded-full px-2 py-0.5 text-xs hover:bg-primary/20 transition-colors"
                              >
                                <TagIcon className="w-2.5 h-2.5" />
                                {pt.tags.name}
                              </button>
                            ))}
                            {post.post_tags.length > 5 && (
                              <span className="text-xs text-muted-foreground">+{post.post_tags.length - 5} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            // Individual blog post view
            <div className="p-4">
              <div className="max-w-[800px] mx-auto">
                <div className="flex flex-col lg:flex-row gap-4 mb-6 items-center lg:items-start">
                  <div className="flex flex-col items-center lg:items-start flex-shrink-0">
                    <div className="w-56 max-h-96 overflow-hidden rounded bg-muted/30">
                      {selectedPost.image_url ? (
                        <img
                          src={selectedPost.image_url}
                          alt={selectedPost.title}
                          className="w-full h-auto max-h-96 object-contain"
                        />
                      ) : (
                        <div className="w-56 h-56 bg-muted/50 flex items-center justify-center opacity-30">
                          <Logo size={96} className="grayscale" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 lg:pl-4 lg:max-w-[60%]">
                    <h1 className="text-xl font-bold mb-2">{selectedPost.title}</h1>
                    <p className="text-sm text-muted-foreground mb-2">
                      By Admin • {formatDate(selectedPost.published_at || selectedPost.created_at)}
                    </p>
                    {/* Tags */}
                    {selectedPost.post_tags && selectedPost.post_tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {selectedPost.post_tags.map((pt) => (
                          <span
                            key={pt.tag_id}
                            className="inline-flex items-center gap-1 bg-primary/10 text-primary border border-primary/20 rounded-full px-3 py-1 text-sm"
                          >
                            <TagIcon className="w-3 h-3" />
                            {pt.tags.name}
                          </span>
                        ))}
                      </div>
                    )}
                    <PostLikes postId={selectedPost.id} content={selectedPost.content} post={selectedPost} />
                    <div
                      className="prose prose-sm max-w-none text-sm leading-relaxed prose-headings:text-foreground prose-p:text-foreground prose-a:text-primary prose-strong:text-foreground prose-em:text-foreground prose-code:text-foreground prose-pre:text-foreground prose-blockquote:text-foreground prose-li:text-foreground prose-img:rounded-md prose-img:max-w-full mt-4"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(selectedPost.content),
                      }}
                    />
                  </div>
                </div>

                {/* Poll section */}
                <PollRenderer postId={selectedPost.id} />

                {/* Comments section */}
                <div ref={commentsRef} className="border-t pt-4">
                  <CommentSection blogPostId={selectedPost.id} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Blog Post</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{postToDelete?.title}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setPostToDelete(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeletePost}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Publish Date Dialog (for republishing) */}
        <AlertDialog open={showPublishDateDialog} onOpenChange={setShowPublishDateDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Update Publish Date?</AlertDialogTitle>
              <AlertDialogDescription>
                This post was previously published. Do you want to update the publish date to now?
                <br />
                <br />
                <strong>Keep Original Date:</strong> Post will be republished with its original date (no email
                notifications).
                <br />
                <br />
                <strong>Update to Now:</strong> Post will be republished with today's date and email notifications will
                be sent.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setShowPublishDateDialog(false);
                  setPostToRepublish(null);
                }}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={() => handleRepublishWithDate(false)} className="bg-secondary hover:bg-secondary/90">
                Keep Original Date
              </AlertDialogAction>
              <AlertDialogAction onClick={() => handleRepublishWithDate(true)}>Update to Now</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Publish Warning Dialog */}
        <AlertDialog open={showPublishWarning} onOpenChange={setShowPublishWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Publish Blog Post?</AlertDialogTitle>
              <AlertDialogDescription>
                This will send email notifications to <strong>{subscriberCount}</strong>
                {subscriberCount === 1 ? " subscriber" : " subscribers"}.
                {subscriberCount > 0 && (
                  <>
                    <br />
                    <br />
                    You can view the notification log in Supabase after publishing.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setShowPublishWarning(false);
                  setPostToPublish(null);
                  setPostToRepublish(null);
                }}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={confirmPublish}>Publish & Send Notifications</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Auth Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
          }}
        />
      </div>
    </div>
  );
};

export default BlogCard;
